import { benefitSelectionTypes } from "constants/benefitSelection";
import {
  isPaymentSetupIncomplete,
  PAYMENT_SETUP_INCOMPLETE_SUFFIX,
} from "pages/CreateOrder/utils/payoutProviderSetup";
import { DropdownChangeParams } from "primereact/dropdown";
import { useDispatch, useSelector } from "react-redux";
import { setRows } from "store/slices/serviceDetails";
import { ProcedureRow } from "types/CreateOrder";

import { StyledDropdown } from "./styled";

interface IProviderDropdown {
  row: ProcedureRow;
  currentProviderProcedures?: any;
  /** CLE-3074 — mark providers whose Finix payment setup is incomplete (Finix accounts only). */
  isFinixAccount?: boolean;
}

/** The provider's name, suffixed on a Finix account when its payment setup is incomplete. */
export const providerOptionLabel = (
  provider: { name: string; finixMerchantId?: string | null },
  isFinixAccount: boolean
): string =>
  isFinixAccount && isPaymentSetupIncomplete(provider)
    ? `${provider.name}${PAYMENT_SETUP_INCOMPLETE_SUFFIX}`
    : provider.name;

export function ProviderDropdown({
  row,
  currentProviderProcedures,
  isFinixAccount = false,
}: IProviderDropdown) {
  const dispatch = useDispatch();
  const rows = useSelector((state: any) => state.serviceDetailsSlice.rows);

  const { providerDropdown, selectedProvider, id } = row;

  if (providerDropdown.length === 1) {
    return <span>{providerOptionLabel(selectedProvider, isFinixAccount)}</span>;
  }

  const handleOnChangeProviderDropdown = (e: DropdownChangeParams) => {
    const newRows = rows.slice() as ProcedureRow[];

    const provider = providerDropdown.find(
      (provider) => provider.id === e.value
    );
    const providerProcedure = currentProviderProcedures?.find(
      (pp: any) =>
        pp.providerId === e.value && pp.procedureId === row.procedureId
    );
    if (provider) {
      const updatedRows = newRows.map((r) => {
        if (
          r.procedureId === row.procedureId &&
          r.cptCode === row.cptCode &&
          r.providerProcedureId === row.providerProcedureId &&
          JSON.stringify(
            [...r.providerDropdown].sort((a, b) => a.id.localeCompare(b.id))
          ) ===
            JSON.stringify(
              [...row.providerDropdown].sort((a, b) => a.id.localeCompare(b.id))
            ) &&
          JSON.stringify(r.selectedProvider) ===
            JSON.stringify(row.selectedProvider)
        ) {
          const { benefitSelectionType } = r;
          // Re-apply the new provider's fee-schedule default only while every row is
          // still on a default (none, Requires ABN, or Requires ABN + Self-Pay).
          const allRowsHaveDefaultBenefitOrRequiresAbn = rows.every(
            (r: ProcedureRow) =>
              !r.benefitSelectionType ||
              r.benefitSelectionType === "REQUIRES_ABN" ||
              r.benefitSelectionType === benefitSelectionTypes.SELF_PAY_WITH_ABN
          );
          let newBenefitSelectionType = benefitSelectionType;
          if (allRowsHaveDefaultBenefitOrRequiresAbn) {
            if (providerProcedure?.requiresAbnSelfPay) {
              newBenefitSelectionType = benefitSelectionTypes.SELF_PAY_WITH_ABN;
            } else {
              newBenefitSelectionType = providerProcedure?.requiresAbn
                ? "REQUIRES_ABN"
                : null;
            }
          }
          return {
            ...r,
            selectedProvider: provider,
            amount: provider.priceInCents,
            providerProcedureId:
              providerProcedure?.id || row.providerProcedureId,
            toBeEdited:
              providerProcedure?.toBeEdited === undefined
                ? null
                : providerProcedure?.toBeEdited,
            benefitSelectionType: newBenefitSelectionType,
          };
        }
        return r;
      });
      dispatch(setRows(updatedRows));
    }
  };

  return (
    <StyledDropdown
      data-testid="providerDropdown_ServiceDetails"
      optionValue="id"
      optionLabel="label"
      id="selectProvider"
      options={providerDropdown.map((provider) => ({
        ...provider,
        label: providerOptionLabel(provider, isFinixAccount),
      }))}
      value={selectedProvider.id}
      placeholder="Select Provider"
      onChange={handleOnChangeProviderDropdown}
    />
  );
}
